import {
    useMutation,
    useQueryClient,
    UseMutationResult,
} from '@tanstack/react-query'
import { updateAttribute } from '../../../../services/dtServices'
import { IAttribute } from '../../interfaces'

export const useUpdateAttribute = (
    orgId: string,
    entityId: string,
): UseMutationResult<
    Awaited<ReturnType<typeof updateAttribute>>,
    Error,
    IAttribute,
    { previousAttributes?: IAttribute[] }
> => {
    const queryClient = useQueryClient()
    const queryKey = ['attributes', entityId, orgId]
    return useMutation({
        mutationFn: (attribute: IAttribute) => {
            return updateAttribute(orgId, entityId, attribute.uid, {
                ...attribute,
                isSearchable: true,
            })
        },
        /**
         * Cancel in-flight fetches before the optimistic edit so a slow GET
         * that started earlier can't resolve afterwards and revert the change
         * (UBQ-5435). Snapshot the list so onError can roll back.
         */
        onMutate: async (newAttribute: IAttribute) => {
            await queryClient.cancelQueries({ queryKey })
            const previousAttributes =
                queryClient.getQueryData<IAttribute[]>(queryKey)
            queryClient.setQueryData(
                queryKey,
                (prevAttributes: IAttribute[] | undefined) =>
                    prevAttributes?.map((prevAttribute: IAttribute) =>
                        prevAttribute.uid === newAttribute.uid
                            ? newAttribute
                            : prevAttribute,
                    ),
            )
            return { previousAttributes }
        },
        onError: (_err, _newAttribute, context) => {
            if (context?.previousAttributes) {
                queryClient.setQueryData(queryKey, context.previousAttributes)
            }
        },
        //refetch attributes after mutation,
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    })
}
